import type { GlossaryGroup } from './types';

/**
 * Operator glossary — the terms that appear on the admin screens, in the words the screens
 * use. Deliberately **not** the same document as `docs/10-glossary.md`: that one is the
 * developer-facing tr↔en mapping with column names and ADR links, this one is for the person
 * running an auction. Keep both in step when a term changes, but do not merge them.
 */
export const GLOSSARY: GlossaryGroup[] = [
  {
    id: 'g-price',
    title: 'Fiyat ve teklif',
    terms: [
      {
        id: 'g-starting-price',
        term: 'Açılış fiyatı',
        short: 'Teklif almanın başladığı tutar — katılımcının gördüğü tek başlangıç fiyatıdır.',
        body: 'Araç kartındaki fiyattan gelir. Lot oluşturulurken o anki değer lotun üzerine yazılır; aracın fiyatını sonradan değiştirmek açık bir lotun açılış fiyatını değiştirmez.',
        see: ['g-reserve', 'h-vehicle-pricing'],
      },
      {
        id: 'g-top-bid',
        term: 'Güncel teklif',
        short: 'Şu ana kadar verilmiş en yüksek teklif.',
        see: ['g-increment'],
      },
      {
        id: 'g-reserve',
        term: 'Rezerv (gizli taban)',
        short: 'Aracın altına satılmayacağı gizli alt sınır. Katılımcı rezervi hiçbir yerde görmez.',
        body: 'Rezerv geçildiği anda lot kendiliğinden kesin satışa döner: artık en yüksek teklifi veren alır. Rezerv geçilmeden süre biterse lot “Satılmadı” olarak kapanır ve havuza düşer. Rezervi canlı konsoldan “Rezerv” düğmesiyle değiştirebilirsiniz.',
        see: ['g-guaranteed', 'g-unsold', 'h-live-reserve', 'f-reserve-visible'],
      },
      {
        id: 'g-guaranteed',
        term: 'Kesin satış',
        short: 'Lot mutlaka satılacak: süre sonunda en yüksek teklifi veren kazanır.',
        body: 'İki yoldan biriyle olur: lot en baştan kesin satış olarak açılır, ya da teklifler rezervi geçtiği an otomatik olarak kesin satışa döner. Kesin satış olarak açılan ama hiç teklif almayan bir lot yine de “Satılmadı” kapanır — satacak teklif yoktur.',
        see: ['g-reserve', 'g-sold'],
      },
      {
        id: 'g-increment',
        term: 'Artış',
        short: 'Bir sonraki teklifin en az ne kadar yukarıda olması gerektiği.',
        body: 'Sabit bir rakam değildir: fiyat yükseldikçe bareme göre büyür. Lot bazında sabit bir artış tanımlanmışsa barem yerine o geçerlidir; rezerv geçildikten sonra ise (tanımlıysa) rezerv sonrası artış devreye girer.',
        see: ['g-barem'],
      },
      {
        id: 'g-barem',
        term: 'Barem',
        short: 'Fiyat aralıklarına göre artış tablosu — ör. 1 M’ye kadar +10.000, 2 M’ye kadar +15.000.',
        body: 'İhale bazında tanımlanır ve teklif anında o anki fiyata göre yeniden hesaplanır; lot açıldıktan sonra fiyat bir üst banda geçtiğinde adım da büyür. Barem tablosu şu an yönetim ekranından düzenlenmez — değişmesi gerekiyorsa teknik ekibe iletin. Tek bir araç için sabit adım isterseniz araç kartındaki “Artış miktarı” alanını kullanın: o alan, barem yerine geçer.',
        see: ['g-increment', 'h-vehicle-pricing'],
      },
      {
        id: 'g-jump',
        term: 'Hedefe yükselt',
        short: 'Bir hedef tutar girersiniz, sistem baremi adım adım uygulayıp hedefi aşmayan en yüksek tutarda tek teklif yazar.',
        body: 'Sonuç bareme uygun normal bir tekliftir; teklif geçmişinde ayrı bir tür olarak görünmez. Hedef tutar, ara adımların hepsi uygulandıktan sonra bile aşılmaz.',
        see: ['g-increment', 'g-free-bid', 'h-live-bidding'],
      },
      {
        id: 'g-free-bid',
        term: 'Serbest fiyat',
        short: 'Bareme uymayan, elle yazılan tutar. Yalnızca Yönetici ve Geliştirici kullanabilir.',
        body: 'Salondan gelen ya da telefonla alınan bir teklifi sisteme girmek için vardır. Barem dışına çıktığı için teklif geçmişinde “serbest teklif” olarak işaretlenir.',
        see: ['g-jump', 'h-live-bidding'],
      },
      {
        id: 'g-deposit',
        term: 'Teminat',
        short: 'Katılımcının teklif verebilmek için önceden yatırdığı tutar.',
        body: 'Varsayılan tutar Ayarlar sayfasından belirlenir. Teminat “Bloke” duruma geçtiğinde katılımcı o ihalede teklif verebilir. Üyelik paketi olan katılımcılar teminat vermeden katılabilir.',
        see: ['g-deposit-states', 'h-refund'],
      },
    ],
  },

  {
    id: 'g-sale-states',
    title: 'İhale durumları',
    terms: [
      {
        id: 'g-draft',
        term: 'Taslak',
        short: 'Kaydedilmiş ama yayınlanmamış ihale. Kimse göremez.',
        see: ['h-sale-create'],
      },
      {
        id: 'g-preparing',
        term: 'Hazırlanıyor',
        short: 'Yayınlanmış; vitrinde görünüyor ama teklif henüz açılmadı.',
        body: 'İhale listeleme tarihinde yayına çıkar, araçlar ise ihale başlangıç tarihinde teklife açılır. Aradaki sürede ihale “Hazırlanıyor” görünür — vitrinde vardır, teklif alınmaz.',
        see: ['g-listing-date', 'f-live-not-bidding'],
      },
      {
        id: 'g-live',
        term: 'Devam ediyor',
        short: 'Araçlar teklife açık; ihale işliyor.',
        see: ['h-live-console'],
      },
      {
        id: 'g-paused',
        term: 'Duraklatıldı',
        short: 'Sayaç dondu, teklif alınmıyor. Katılımcılar durumu görür, nedenini görmez.',
        body: 'Giriş yapmış katılımcılara “Duraklatıldı” olarak gösterilir; nedeni hiçbir ekranda yazmaz. Devam ettirdiğinizde takvim yeniden hesaplanır.',
        see: ['h-live-pause', 'f-pause-visible'],
      },
      {
        id: 'g-closed',
        term: 'Tamamlandı',
        short: 'Bütün araçlar kapandı; kazananlar belli.',
        see: ['h-export'],
      },
      {
        id: 'g-cancelled',
        term: 'İptal',
        short: 'İhale yönetici tarafından iptal edildi.',
        body: 'Kayıt silinmez, iptal olarak işaretlenir; denetim kaydında ve geçmiş listelerinde görünmeye devam eder.',
      },
    ],
  },

  {
    id: 'g-lot-states',
    title: 'Araç (lot) durumları',
    terms: [
      {
        id: 'g-scheduled',
        term: 'Sırada',
        short: 'Araç ihaleye eklendi ama henüz teklife açılmadı.',
      },
      {
        id: 'g-open',
        term: 'Açık',
        short: 'Araç şu anda teklif alıyor.',
      },
      {
        id: 'g-sold',
        term: 'Satıldı',
        short: 'Rezerv geçildi ya da kesin satış lotu teklif alarak kapandı; kazanan belli.',
        see: ['g-reserve', 'g-guaranteed'],
      },
      {
        id: 'g-unsold',
        term: 'Satılmadı',
        short: 'Rezerv geçilmeden kapandı. Araç havuza döner ve aynı ihaleye tekrar eklenebilir.',
        see: ['h-live-readd', 'f-readd'],
      },
      {
        id: 'g-removed',
        term: 'Çıkarıldı',
        short: 'Araç yönetici tarafından işleyen ihaleden çıkarıldı.',
        see: ['h-live-remove'],
      },
    ],
  },

  {
    id: 'g-schedule',
    title: 'Zamanlama',
    terms: [
      {
        id: 'g-listing-date',
        term: 'Listeleme tarihi',
        short: 'İhalenin vitrinde görünmeye başladığı an.',
        see: ['g-start-date', 'h-sale-edit-window'],
      },
      {
        id: 'g-start-date',
        term: 'İhale başlangıcı',
        short: 'İlk aracın teklife açıldığı an. Listeleme tarihinden günler sonra olabilir.',
        see: ['g-listing-date', 'f-live-not-bidding'],
      },
      {
        id: 'g-end-date',
        term: 'İhale bitişi',
        short: 'Son aracın kapanacağı an. Son saniye uzatmaları bu tarihi ileri çekebilir.',
        see: ['g-soft-close'],
      },
      {
        id: 'g-styles',
        term: 'İhale düzeni',
        short: 'Araçların sıraya nasıl dizileceği: Paralel, Sıralı, Sıralı (10 dk), Sıralı (aralıklı).',
        body: 'Paralel: bütün araçlar aynı anda açılır. Sıralı: her araç sırayla, arka arkaya. Sıralı (10 dk): her araca 10 dakika. Sıralı (aralıklı): araçlar arasında bekleme süresi bırakılır. Düzen ihale tanımlanırken seçilir ve takvimin tamamını belirler.',
        see: ['h-sale-create'],
      },
      {
        id: 'g-take-control',
        term: 'Kontrolü al',
        short: 'Lotu elle yönetime alır: son saniye uzatması ve çekiç bu moddayken çalışır.',
        body: 'Kontrol alınmamış lotlar süresi dolunca kendiliğinden kapanır — bu her zaman geçerli olan güvenlik ağıdır. Kontrolü aldığınızda bu otomatik kapanış hâlâ yedekte durur, üstüne son saniye uzatması ve elle çekiç yetkisi eklenir.',
        see: ['g-soft-close', 'g-hammer', 'h-live-hammer'],
      },
      {
        id: 'g-soft-close',
        term: 'Son saniye uzatması',
        short: 'Bitişe çok yakın gelen teklif, bitişi o kadar süre ileri atar.',
        body: 'Yalnızca kontrolü alınmış lotlarda çalışır ve son dakika kapmasını (sniping) engeller. Sıralı düzende uzatma, arkadaki araçları da aynı kadar kaydırır; ihalenin bitiş tarihi de buna göre güncellenir.',
        see: ['g-take-control', 'g-end-date'],
      },
      {
        id: 'g-hammer',
        term: 'Çekiç',
        short: 'Açık lotu süresi dolmadan elle kapatır.',
        body: 'Yalnızca ihale “Devam ediyor” durumundayken, lot açıkken ve o lotun kontrolü alınmışken görünür. Kapanış normal kapanışla aynıdır: kazanan belirlenir, satıldı/satılmadı sonucu yazılır.',
        see: ['g-take-control', 'h-live-hammer', 'f-hammer-missing'],
      },
    ],
  },

  {
    id: 'g-payment',
    title: 'Ödeme ve teminat',
    terms: [
      {
        id: 'g-deposit-states',
        term: 'Teminat durumları',
        short: 'Beklemede → Bloke → Tahsil edildi / İade edildi / İptal.',
        body: '“Bloke”, paranın kartta tutulduğu ve katılımcının teklif verebildiği durumdur. “Tahsil edildi” ihale sonrası tahsilatı, “İade edildi” paranın geri gönderilmesini, “İptal” ise tahsilat yapılmadan bloğun kaldırılmasını anlatır.',
        see: ['g-refund', 'h-refund'],
      },
      {
        id: 'g-3ds',
        term: '3D güvenli ödeme',
        short: 'Kart doğrulaması için bankaya yönlendirme adımı.',
        body: 'Katılımcı bankadan dönmeden ödeme “3DS sürüyor” durumunda takılı kalabilir. Bu kayıtlar günlük mutabakat taramasıyla toparlanır, kullanıcı sayfasından da elle sonuçlandırılabilir.',
        see: ['h-payments', 'f-stuck-3ds'],
      },
      {
        id: 'g-refund',
        term: 'İade / iptal',
        short: 'Para henüz bankadan çekilmediyse iptal, çekildiyse iade edilir. Seçimi sistem yapar.',
        body: 'İade düğmesine bastığınızda uygun yöntem otomatik seçilir; siz “iptal mi iade mi” diye karar vermezsiniz. İşlem gerçek para hareketidir ve geri alınamaz.',
        see: ['h-refund', 'g-reconcile'],
      },
      {
        id: 'g-reconcile',
        term: 'Mutabakat',
        short: 'Bizdeki kayıt ile bankadaki kaydın örtüştüğünün doğrulanması.',
        body: 'Bir teminat bizde sonuçlanmış ama ödeme tarafı hâlâ açık görünüyorsa satırda bir uyarı rozeti çıkar. Günlük tarama bu kayıtları kendiliğinden toparlar; sipariş numarasıyla bankada aratabilirsiniz.',
        see: ['h-payments'],
      },
      {
        id: 'g-membership',
        term: 'Üyelik paketi',
        short: 'Ücretli üyelik: sahibi araç başına teminat yatırmadan teklif verebilir.',
        body: 'Paketler ve fiyatları Ayarlar sayfasından yönetilir. Üyelik teminat şartını kaldırır; teklif verme kurallarının geri kalanı (ihalenin açık olması, aracın sırası) aynen geçerlidir.',
        see: ['h-config', 'g-deposit'],
      },
      {
        id: 'g-early-bid',
        term: 'Erken teklif',
        short: 'İhale vitrine çıktıktan sonra, araç sırası gelmeden önce verilen teklif.',
        body: 'Listeleme tarihi ile aracın açılış anı arasındaki pencerede verilir. Üyeliğe özel değildir: teminatı bloke olan katılımcı da erken teklif verebilir. Teklif geçmişinde “erken teklif” olarak işaretlenir.',
        see: ['g-listing-date', 'g-start-date', 'g-deposit'],
      },
    ],
  },

  {
    id: 'g-misc',
    title: 'Araç, başvuru ve kayıt',
    terms: [
      {
        id: 'g-expertise',
        term: 'Ekspertiz raporu',
        short: 'Aracın 14 parçasının durumu: orijinal, değişen, boyalı, lokal boyalı, ezik, çizik, kayıtlı.',
        body: '“Orijinal” rozeti ayrı bir alan değildir: bütün parçalar orijinal işaretlendiğinde kendiliğinden görünür. Tek bir parça bile başka bir durumdaysa rozet çıkmaz.',
        see: ['h-vehicle-expertise', 'f-original-badge'],
      },
      {
        id: 'g-cover',
        term: 'Kapak görseli',
        short: 'Aracın listelerde ve vitrinde görünen ana fotoğrafı.',
        see: ['h-vehicle-media'],
      },
      {
        id: 'g-public-media',
        term: 'Herkese açık görsel',
        short: 'Görselin yayımlanabilir olduğunu belirten işaret; şu anda erişim vermez.',
        body: 'Bu işaret, bir fotoğrafın dışarıya gösterilmesinde sakınca olmadığını kaydeder. 13.08.2026’dan beri açık artırma sayfalarının tamamı üyelere özel olduğu için işaretin verdiği bir erişim yoktur: giriş yapmamış ziyaretçi hiçbir araç fotoğrafını göremez, giriş yapmış üye ise aracın bütün görsellerini görür. İşareti gene de doğru bırakın — ileride herkese açık bir tanıtım sayfası yapılırsa hangi görsellerin kullanılabileceği buradan okunacak.',
        see: ['g-company-gated', 'f-photo-not-public'],
      },
      {
        id: 'g-company-gated',
        term: 'Firmaya özel ihale',
        short: 'Yalnızca seçilen firmaların görüp katılabildiği ihale.',
        body: 'Firmaya özel ihaleler genel ihale listesine hiç düşmez: yalnızca seçtiğiniz firmaların üyeleri erişir, diğer üyeler ne ihaleyi ne araçlarını görür. Giriş yapmamış ziyaretçi hiçbir ihaleyi görmez.',
        see: ['h-sale-create'],
      },
      {
        id: 'g-api-key',
        term: 'API anahtarı',
        short: 'Bir iş ortağı firmanın kendi sisteminden araç gönderirken kullandığı gizli anahtar.',
        body: 'Anahtar kişiye değil **firmaya** aittir: firmanın personeli değişse de entegrasyon çalışmaya devam eder, ve bir firmanın birden çok sistemi için birden çok anahtarı olabilir. Anahtarı Firmalar sayfasından siz üretir ve firmaya iletirsiniz; açık hâli yalnızca üretildiği anda bir kez görünür, sonra kurtarılamaz. İptal anında geçerlidir. Firmayı arşivlemek bütün anahtarlarını iptal eder ve arşivden çıkarmak onları geri getirmez.',
        see: ['h-firms', 'g-submission'],
      },
      {
        id: 'g-submission',
        term: 'Başvuru',
        short: 'İş ortağının kendi sisteminden gönderdiği araç kaydı; onaylanana kadar yayına çıkmaz.',
        body: 'Başvurular Başvurular sayfasında sıraya düşer. Onayladığınızda araç, yayınlanmış ihaleye “Sırada” bir lot olarak eklenir; reddettiğinizde ihaleye hiç girmez.',
        see: ['h-submissions'],
      },
      {
        id: 'g-approval-pending',
        term: 'Onay bekleyen üyelik',
        short: 'Siteden kendi kaydını yapmış, ama henüz bir yönetici tarafından onaylanmamış hesap.',
        body: 'Kayıt formunu dolduran herkes doğrudan içeri girmez: hesap “onay bekliyor” durumunda sıraya düşer, giriş yapamaz ve teklif veremez. Kullanıcılar sayfasındaki **Bekleyen** sekmesinden onaylar ya da reddedersiniz; onayladığınızda başvuru sahibine e-posta gider. Yöneticinin kendi açtığı hesaplar bu sıraya girmez, doğrudan onaylı başlar.',
        see: ['h-users-approval', 'g-membership'],
      },
      {
        id: 'g-showcase-counter',
        term: 'Vitrin sayacı',
        short: 'Giriş yapmamış ziyaretçinin gördüğü toplam rakam: yapılan ihale ve satılan araç sayısı.',
        body: 'Ziyaretçiye gösterilen sayı **başlangıç değeri + gerçek sayı** olarak hesaplanır; başlangıç değerini Ayarlar sayfasından siz belirlersiniz ve dışarıdan görünmez. Amaç rakamları gizlemek değil, nereden sayılmaya başlandığını seçmek: yeni bir platformun gerçek rakamları ilk kez giren birine olduğundan kötü görünebilir. Sayaçlar varsayılan olarak kapalıdır ve açılmadıkça vitrinde hiç görünmez.',
        see: ['h-showcase-counters', 'h-config'],
      },
      {
        id: 'g-conference',
        term: 'Konferans',
        short: 'İhale sırasındaki canlı yayın odası. Ortak (tüm ihale) ya da araç bazlı olabilir.',
        body: 'Oda ihale başlamadan kısa süre önce hazırlanır ve canlı konsolda bağlantısı çıkar. Yayın kaydı uyuşmazlık kanıtı olarak saklanır.',
        see: ['h-live-conference'],
      },
      {
        id: 'g-audit',
        term: 'Denetim kaydı',
        short: 'Kimin ne zaman ne yaptığının değiştirilemez kaydı.',
        body: 'Rezerv değişikliği, teminat iadesi, lot çıkarma, kullanıcı düzenleme gibi işlemler buraya yazılır. İhale ve kullanıcı sayfalarından o kayda doğrudan gidebilirsiniz.',
        see: ['h-audit'],
      },
    ],
  },
];
